'use client';

import type {
  ComponentProps,
  ComponentType,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography, { type TypographyProps } from '@mui/material/Typography';

import dynamic from 'next/dynamic';
import remarkBreaks from 'remark-breaks';
// eslint-disable-next-line import/no-extraneous-dependencies
import remarkGfm from 'remark-gfm';

import mixinSx from '@watershed/style/mixinSx';

import BasicTableComponents from './BasicTableComponents';
import { inlineCodeSx } from './InlineCode';
import TextLink from './TextLink';

const ReactMarkdown: ComponentType<import('react-markdown').Options> = dynamic(
  () => import('react-markdown')
);

type MarkdownBaseProps = {
  readonly children?: ReactNode;
  readonly className?: string;
  readonly node?: unknown;
  readonly ref?: unknown;
};

type MarkdownAnchorProps = MarkdownBaseProps & {
  readonly href?: string;
  readonly title?: string;
};

type MarkdownTableCellProps = MarkdownBaseProps & {
  readonly align?: 'center' | 'char' | 'inherit' | 'justify' | 'left' | 'right';
};

type MarkdownComponentOptions = {
  readonly variant: TypographyProps['variant'];
  readonly onClickLink?: (evt: ReactMouseEvent<HTMLAnchorElement>) => void;
  readonly linkOpensNewTab?: boolean;
};

export const customReactMarkdownSx: SxProps<Theme> = {
  '&': {
    // This is the default, resetting to be defensive against e.g. Mui's Tooltip which
    // sets it to pre-line, which frequently looks weird.
    whiteSpace: 'normal',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginBlockStart: 2.5,
    marginBlockEnd: 1,
  },
  '& h1:first-child': {
    marginBlockStart: 0,
  },
  '& hr': {
    marginBlock: 2,
  },

  '& details': {
    marginTop: 2,
    '& summary': {
      color: (theme) => theme.palette.text.primary,
    },
  },
  '& iframe': {
    width: '100%',
    maxWidth: '100%',
    border: 0,
    margin: (theme) => theme.spacing(1, 0, 2),
  },
  '& p + p': {
    marginBlockStart: 1,
  },
  '& p, & li': {
    wordWrap: 'break-word',
  },
  '& ul, & ol': {
    marginBlock: 1,
    paddingLeft: 2,
  },
  '& ul:has(input[type="checkbox"])': {
    listStyle: 'none',
    paddingLeft: 0,
  },
  '& code': {
    ...inlineCodeSx,
    whiteSpace: 'pre-wrap',
  },
};

const getTableCellAlign = (
  align: MarkdownTableCellProps['align']
): 'center' | 'inherit' | 'justify' | 'left' | 'right' | undefined => {
  if (align === 'char') {
    return undefined;
  }
  return align;
};

/**
 * Returns the shared markdown renderers used across ReactMarkdown-based views.
 * Consumers can spread this object and override only the tags they need.
 */
export function createMarkdownComponents({
  variant,
  onClickLink,
  linkOpensNewTab,
}: MarkdownComponentOptions) {
  return {
    h1: ({ children, className }: MarkdownBaseProps) => (
      <Typography variant="h1" className={className}>
        {children}
      </Typography>
    ),
    h2: ({ children, className }: MarkdownBaseProps) => (
      <Typography variant="h2" className={className}>
        {children}
      </Typography>
    ),
    h3: ({ children, className }: MarkdownBaseProps) => (
      <Typography variant="h4" className={className}>
        {children}
      </Typography>
    ),
    h4: ({ children, className }: MarkdownBaseProps) => (
      <Typography variant="h5" className={className}>
        {children}
      </Typography>
    ),
    h5: ({ children, className }: MarkdownBaseProps) => (
      <Typography variant="h5" className={className}>
        {children}
      </Typography>
    ),
    h6: ({ children, className }: MarkdownBaseProps) => (
      <Typography variant="h6" className={className}>
        {children}
      </Typography>
    ),
    a: ({ children, href, title, className }: MarkdownAnchorProps) => (
      <TextLink
        href={href}
        title={title}
        className={className}
        onClick={onClickLink}
        newTab={linkOpensNewTab}
      >
        {children}
      </TextLink>
    ),
    p: ({ children, className }: MarkdownBaseProps) => (
      <Typography
        variant={variant}
        // Necessary to make explicit, because `body3` variant defaults to `span`.
        component="p"
        className={className}
      >
        {children}
      </Typography>
    ),
    ul: ({ children, className }: MarkdownBaseProps) => (
      <Box component="ul" className={className}>
        {children}
      </Box>
    ),
    ol: ({ children, className }: MarkdownBaseProps) => (
      <Box component="ol" className={className}>
        {children}
      </Box>
    ),
    li: ({ children, className }: MarkdownBaseProps) => (
      <Typography component="li" variant={variant} className={className}>
        {children}
      </Typography>
    ),
    hr: ({ className }: MarkdownBaseProps) => <Divider className={className} />,
    table: ({ children, className }: MarkdownBaseProps) => (
      <TableContainer
        className={className}
        sx={{
          marginBlock: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <BasicTableComponents.BasicTable>
          {children}
        </BasicTableComponents.BasicTable>
      </TableContainer>
    ),
    thead: ({ children, className }: MarkdownBaseProps) => (
      <BasicTableComponents.BasicTableHead className={className}>
        {children}
      </BasicTableComponents.BasicTableHead>
    ),
    tbody: ({ children, className }: MarkdownBaseProps) => (
      <TableBody className={className}>{children}</TableBody>
    ),
    tr: ({ children, className }: MarkdownBaseProps) => (
      <TableRow className={className}>{children}</TableRow>
    ),
    th: ({ children, align, className }: MarkdownTableCellProps) => (
      <TableCell
        component="th"
        align={getTableCellAlign(align)}
        className={className}
      >
        {children}
      </TableCell>
    ),
    td: ({ children, align, className }: MarkdownTableCellProps) => (
      <TableCell
        component="td"
        align={getTableCellAlign(align)}
        className={className}
      >
        {children}
      </TableCell>
    ),
    pre: ({ children, className }: MarkdownBaseProps) => (
      <Box component="pre" className={className}>
        {children}
      </Box>
    ),
  };
}

export default function CustomReactMarkdown({
  source,
  variant = 'body2' as TypographyProps['variant'],
  className,
  sx,
  onClickLink,
  linkOpensNewTab,
  ...reactMarkdownProps
}: {
  source: LocalizedString | null;
  variant?: TypographyProps['variant'];
  className?: TypographyProps['className'];
  sx?: SxProps<Theme>;
  onClickLink?: (evt: ReactMouseEvent<HTMLAnchorElement>) => void;
  linkOpensNewTab?: boolean;
} & Omit<ComponentProps<typeof ReactMarkdown>, 'children'>) {
  if (!source) {
    return <></>;
  }

  const components = createMarkdownComponents({
    variant,
    onClickLink,
    linkOpensNewTab,
  });

  return (
    <Box sx={mixinSx(customReactMarkdownSx, sx)} className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}
        {...reactMarkdownProps}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
}
